import {
  IContactMessagesResponse,
  IHandleGetContactMessagesParams,
  IHandleReplyInquiryParams,
  IHandleResolveInquiryParams,
  IInquiryProps,
  IReplyInquiryFormData,
  IReplyInquiryResponse,
} from "@model/contactus.model";
import { AxiosCalls } from "./Functions/AxiosCalls";
import { handleDefaultApiErrors } from "Utils/Global/apiErrorHandling";
import {
  IContactFormData,
  IContactResponse,
  IHandleContactSubmitParams,
} from "@model/MainPages.model";
export class ContactService {
  private static readonly RE_PREFIX = "RE: ";
  static ensureRePrefix(val: string): string {
    // If user deletes "RE:" or edits before it, always restore it.
    if (!val.startsWith(ContactService.RE_PREFIX)) {
      const cleaned = val.replace(/^RE:\s*/i, "").trimStart();
      return `${ContactService.RE_PREFIX}${cleaned}`;
    }
    return val;
  }
  async handleContactSubmit({
    data,
    reset,
    setApiError,
    setLoading,
  }: IHandleContactSubmitParams): Promise<string | null> {
    setLoading(true);
    setApiError(null);

    try {
      const resp = await AxiosCalls.Post<IContactResponse, IContactFormData>(
        "/contact/send",
        data,
        ""
      );

      reset();
      return resp?.data?.message;
    } catch (err) {
      setApiError(handleDefaultApiErrors(err));
      return null;
    } finally {
      setLoading(false);
    }
  }
  async handleReplyToInquiry({
    contactId,
    data,
    token,
    reset,
    setApiError,
    setLoading,
    onAuthError,
    onForbidden,
  }: IHandleReplyInquiryParams): Promise<string | null> {
    setLoading(true);
    setApiError(null);

    try {
      const resp = await AxiosCalls.Post<
        IReplyInquiryResponse,
        IReplyInquiryFormData
      >(`/contact/reply/${contactId}`, data, token);

      reset();
      return resp?.data?.message ?? null;
    } catch (err) {
      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError,
        onForbidden
      });
      setApiError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }

  async handleResolveInquiry({
    id,
    setApiError,
    token,
    onSuccess,
    onAuthError,
    onForbidden,
  }: IHandleResolveInquiryParams): Promise<void> {
    setApiError(null);

    try {
      await AxiosCalls.Post<void, {}>(`/contact/resolve/${id}`, {}, token);

      onSuccess?.();
    } catch (err) {
      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError,
        onForbidden
      });
      setApiError(msg);
    }
  }

  async handleGetContactMessages({
    page,
    perPage,
    q,
    statusFilter,
    sortBy,
    order,
    setLoading,
    setApiError,
    setItems,
    setTotal,
    setPage,
    setPerPage,
    token,
    setResolvedTotal,
    setUnresolvedTotal,
    onAuthError,
    onForbidden

  }: IHandleGetContactMessagesParams): Promise<void> {
    setLoading(true);
    setApiError(null);

    try {
      const query = new URLSearchParams({ page: String(page) });
      if (typeof perPage === "number") query.set("perPage", String(perPage));
      if (q && q.trim()) query.set("q", q.trim());
      if (statusFilter && statusFilter !== "all")
        query.set("resolved", statusFilter);

      if (sortBy) query.set("sortBy", sortBy);
      if (order) query.set("order", order);

      const response = await AxiosCalls.Get<IContactMessagesResponse | any>(
        `/contact/contact-messages?${query.toString()}`, token
      );

      const payload = response?.data ?? {};

      const itemsRaw = payload?.data?.items ?? payload?.items ?? [];

      const normalized: IInquiryProps[] = itemsRaw.map(mapInquiry);
      setItems(normalized);

      const meta = payload?.meta ?? {};
      if (typeof meta.total === "number") setTotal?.(meta.total);
      if (typeof meta.page === "number") setPage?.(meta.page);
      if (typeof meta.perPage === "number") setPerPage?.(meta.perPage);
      if (typeof meta.resolvedTotal === "number")
        setResolvedTotal?.(meta.resolvedTotal);
      if (typeof meta.unresolvedTotal === "number")
        setUnresolvedTotal?.(meta.unresolvedTotal);
    } catch (err) {
      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError,
        onForbidden
      });
      setApiError(msg);
      setItems([]);
      setTotal?.(0);
      setResolvedTotal?.(0);
      setUnresolvedTotal?.(0);
    } finally {
      setLoading(false);
    }
  }
}

const mapInquiry = (raw: any): IInquiryProps => {
  const first = (raw.firstName ?? "").trim();
  const last = (raw.lastName ?? "").trim();
  const full = [first, last].filter(Boolean).join(" ").trim();

  return {
    id: raw.id,
    subject: raw.subject,
    name: (full || raw.name || "Unknown").trim(),
    email: (raw.email ?? "").trim(),
    phone: (raw.phone ?? raw.phoneNumber ?? null) as string | null,
    message: raw.message ?? raw.content ?? "",
    createdAt: raw.createdAt ?? raw.created_at ?? new Date().toISOString(),
    resolved: !!raw.isResolved, // ← direct, no fallback logic needed
  };
};
